/* eslint-disable indent */
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  TextField,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Check } from '@mui/icons-material'

import { attributesNames, comparisonOperators, dialogSize, textFieldTypes, variantTypes } from '../../../constants'
import { Logger, LoggerTypes } from '../../../utils'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  iconNames,
  Button,
  buttonModes,
  buttonTypes,
  FormTitle
} from '../../../components'
import './style.scss'

/**
 * Display the Interaction dialog to import in tasks either a `variable` or a `resource`
 *
 * @param {boolean} openDialog is the dialog popin open
 * @param {func} setOpenDialog function to open/close the dialog
 * @param {Array<object>} variables scenario variables
 * @param {Array<object>} resources scenario resources
 * @param {Array<object>} parameters scenario parameters
 * @returns {Dialog}
 */
export const SimulationConditionDialog = ({
  variables,
  resources,
  parameters,
  openDialog,
  setOpenDialog,
  onSave,
  defaultCondition
}) => {
  const { t } = useTranslation()

  const [accordionExpandedLeft, setAccordionExpandedLeft] = useState(`left-${attributesNames.RESOURCES}`)
  const [accordionExpandedRight, setAccordionExpandedRight] = useState(`right-${attributesNames.RESOURCES}`)

  const [left_operand, setLeftOperand] = useState(defaultCondition?.left_operand || null)
  const [operator, setOperator] = useState(defaultCondition?.operator || comparisonOperators.EQUAL)
  const [right_operand, setRightOperand] = useState(defaultCondition?.right_operand || null)
  const [right_operand_value, setRightOperandValue] = useState(defaultCondition?.right_operand_value || null)

  // --

  const onDialogClose = (e) => {
    if (e) {
      e.preventDefault()
    }
    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationConditionDialog.onDialogClose()')
    setOpenDialog(false)
  }

  const onChangeAccordionLeft = (panel) => (e, newExpanded) => {
    e.preventDefault()
    setAccordionExpandedLeft(newExpanded ? panel : false)

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationConditionDialog.onChangeAccordionLeft()', { newExpanded })
  }

  const onChangeAccordionRight = (panel) => (e, newExpanded) => {
    e.preventDefault()
    setAccordionExpandedRight(newExpanded ? panel : false)

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationConditionDialog.onChangeAccordionRight()', { newExpanded })
  }

  /**
   * Left selector
   * @param {string} attributesName can be either `resources` or `variable` or `parameter`
   * @param {object} attribute  selected attribute.
   * @return {void}
   */

  const onLeftSelect = async (attributesName, attribute) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationConditionDialog.onLeftSelect()', { attributesName, attribute })
    await setLeftOperand({
      attributesName,
      attribute
    })
  }

  const onRightSelect = async (attributesName, attribute) => {
    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationConditionDialog.onRightSelect()', { attributesName, attribute })
    await setRightOperand({
      attributesName,
      attribute
    })
    await setRightOperandValue(null)
  }

  const onRightOperandValueChange = async (e) => {
    e.preventDefault()
    const _value = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationConditionDialog.onRightOperandValueChange()', {
      right_operand_value: _value
    })

    await setRightOperandValue(_value)
    await setRightOperand(null)
  }

  const onConditionSave = async (e) => {
    e.preventDefault()

    const newCondition = {
      left_operand,
      operator,
      right_operand,
      right_operand_value
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'SimulationConditionDialog.onConditionSave()', { newCondition })

    await onSave(newCondition)
    await setOpenDialog(false)
  }

  // Logger.trace(LoggerTypes.CONTAINER, 'SimulationConditionDialog.render()', {
  //   openDialog,
  //   variables,
  //   resources,
  //   parameters,
  //   check: {
  //     left_operand,
  //     operator,
  //     right_operand,
  //     right_operand_value
  //   }
  // })

  return (
    openDialog && (
      <Dialog
        fullWidth={true}
        maxWidth={dialogSize.LARGE}
        open={openDialog}
        onClose={onDialogClose} // <-- allow to close the popin while clicking on background
        aria-labelledby="form-dialog-title"
        className={'SimulationConditionDialog'}
      >
        <DialogContent>
          <FormTitle title={t('flowActions.conditions.dialog.title')} style={{ fontSize: '20px' }} />
          <Grid container spacing={{ xs: 1, md: 1 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            {/* === COLUMN 01 === */}
            <Grid item xs={0} sm={0} md={5}>
              {/*  -- RESOURCES --  */}
              {resources && (
                <Accordion
                  expanded={accordionExpandedLeft === `left-${attributesNames.RESOURCES}`}
                  onChange={onChangeAccordionLeft(`left-${attributesNames.RESOURCES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.RESOURCES}-condition-content`}
                    id={`${attributesNames.RESOURCES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.RESOURCE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.resources')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {resources.map((resource, index) => (
                        <MenuItem
                          key={`resource-${index}`}
                          onClick={() => onLeftSelect(attributesNames.RESOURCES, resource)}
                          className={resource.id === left_operand?.attribute?.id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{resource.id === left_operand?.attribute?.id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{resource.name}</span>{' '}
                              <span className="quantity">({resource.quantity})</span>
                            </div>
                            <div className="description">{resource.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}
              {/*  -- VARIABLES --  */}
              {variables && (
                <Accordion
                  expanded={accordionExpandedLeft === `left-${attributesNames.VARIABLES}`}
                  onChange={onChangeAccordionLeft(`left-${attributesNames.VARIABLES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.VARIABLES}-condition-content`}
                    id={`${attributesNames.VARIABLES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.VARIABLE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.variables')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {variables.map((variable, index) => (
                        <MenuItem
                          key={`variable-${index}`}
                          onClick={() => onLeftSelect(attributesNames.VARIABLES, variable)}
                          className={variable.id === left_operand?.attribute?.id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{variable.id === left_operand?.attribute?.id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{variable.name}</span>{' '}
                              <span className="quantity">
                                ({variable.initial_value} {variable.unit})
                              </span>
                            </div>
                            <div className="description">{variable.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}
              {/*  -- PARAMETERS --  */}
              {parameters && (
                <Accordion
                  expanded={accordionExpandedLeft === `left-${attributesNames.PARAMETERS}`}
                  onChange={onChangeAccordionLeft(`left-${attributesNames.PARAMETERS}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.PARAMETERS}-condition-content`}
                    id={`${attributesNames.PARAMETERS}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.PARAMETER} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.parameters')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {parameters.map((parameter, index) => (
                        <MenuItem
                          key={`parameter-${index}`}
                          onClick={() => onLeftSelect(attributesNames.PARAMETERS, parameter)}
                          className={parameter.id === left_operand?.attribute?.id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{parameter.id === left_operand?.attribute?.id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{parameter.name}</span>{' '}
                              <span className="quantity">
                                ({parameter.value} {parameter.unit})
                              </span>
                            </div>
                            <div className="description">{parameter.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}
            </Grid>

            {/* === COLUMN 02 === */}
            <Grid item xs={0} sm={0} md={2}>
              <Accordion expanded={true} style={{ height: '100%' }}>
                <AccordionDetails>
                  <MenuList dense>
                    {Object.values(comparisonOperators).map((_operator, index) => (
                      <MenuItem
                        key={`operator-${index}`}
                        onClick={() => setOperator(_operator)}
                        className={_operator === operator ? 'lineSelected' : ''}
                      >
                        <ListItemIcon>{_operator === operator ? <Check /> : null}</ListItemIcon>
                        <ListItemText inset className="operator">
                          {_operator}
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </MenuList>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* === COLUMN 03 === */}
            <Grid item xs={0} sm={0} md={5}>
              {/*  -- RESOURCES --  */}
              {resources && (
                <Accordion
                  expanded={accordionExpandedRight === `right-${attributesNames.RESOURCES}`}
                  onChange={onChangeAccordionRight(`right-${attributesNames.RESOURCES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.RESOURCES}-condition-content`}
                    id={`${attributesNames.RESOURCES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.RESOURCE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.resources')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {resources.map((resource, index) => (
                        <MenuItem
                          key={`resource-${index}`}
                          onClick={() => onRightSelect(attributesNames.RESOURCES, resource)}
                          className={resource.id === right_operand?.attribute?.id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{resource.id === right_operand?.attribute?.id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{resource.name}</span>{' '}
                              <span className="quantity">({resource.quantity})</span>
                            </div>
                            <div className="description">{resource.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}

              {/*  -- VARIABLES --  */}
              {variables && (
                <Accordion
                  expanded={accordionExpandedRight === `right-${attributesNames.VARIABLES}`}
                  onChange={onChangeAccordionRight(`right-${attributesNames.VARIABLES}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.VARIABLES}-condition-content`}
                    id={`${attributesNames.VARIABLES}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.VARIABLE} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.variables')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {variables.map((variable, index) => (
                        <MenuItem
                          key={`variable-${index}`}
                          onClick={() => onRightSelect(attributesNames.VARIABLES, variable)}
                          className={variable.id === right_operand?.attribute?.id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>{variable.id === right_operand?.attribute?.id ? <Check /> : null}</ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{variable.name}</span>{' '}
                              <span className="quantity">
                                ({variable.initial_value} {variable.unit})
                              </span>
                            </div>
                            <div className="description">{variable.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}

              {/*  -- PARAMETERS --  */}
              {parameters && (
                <Accordion
                  expanded={accordionExpandedRight === `right-${attributesNames.PARAMETERS}`}
                  onChange={onChangeAccordionRight(`right-${attributesNames.PARAMETERS}`)}
                >
                  <AccordionSummary
                    aria-controls={`${attributesNames.PARAMETERS}-condition-content`}
                    id={`${attributesNames.PARAMETERS}-condition-header`}
                  >
                    <Typography>
                      <Icon name={iconNames.PARAMETER} size={14} />
                      <span className="iconAccordion">{t('flowActions.conditions.dialog.accordion.parameters')}</span>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MenuList dense>
                      {parameters.map((parameter, index) => (
                        <MenuItem
                          key={`parameter-${index}`}
                          onClick={() => onRightSelect(attributesNames.PARAMETERS, parameter)}
                          className={parameter.id === right_operand?.attribute?.id ? 'lineSelected' : ''}
                        >
                          <ListItemIcon>
                            {parameter.id === right_operand?.attribute?.id ? <Check /> : null}
                          </ListItemIcon>
                          <ListItemText inset>
                            <div>
                              <span className="name">{parameter.name}</span>{' '}
                              <span className="quantity">
                                ({parameter.value} {parameter.unit})
                              </span>
                            </div>
                            <div className="description">{parameter.description}</div>
                          </ListItemText>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </AccordionDetails>
                </Accordion>
              )}

              {/*  -- CUSTOM VALUE --  */}
              <Accordion
                expanded={accordionExpandedRight === 'right-custom-value'}
                onChange={onChangeAccordionRight('right-custom-value')}
              >
                <AccordionSummary aria-controls={'custom-value-condition-content'} id={'custom-value-condition-header'}>
                  <Typography>
                    <Icon name={iconNames.CUSTOM} size={14} />
                    <span className="iconAccordion">
                      {t('flowActions.conditions.dialog.accordion.custom_value.title')}
                    </span>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MenuList dense>
                    <MenuItem
                      onClick={() => onRightSelect(null, null)}
                      className={right_operand?.attribute === null ? 'lineSelected' : ''}
                    >
                      <ListItemIcon>{right_operand?.attribute === null ? <Check /> : null}</ListItemIcon>
                      <ListItemText inset>
                        <TextField
                          type={textFieldTypes.NUMBER}
                          name="right_operand_value"
                          id="right_operand_value"
                          value={right_operand_value ? right_operand_value : '0'}
                          variant={variantTypes.FILLED}
                          autoFocus={true}
                          onFocus={(e) => {
                            e.target.select()
                          }}
                          className="common_fullwidth customValue"
                          onChange={onRightOperandValueChange}
                          placeholder={t('flowActions.conditions.dialog.accordion.custom_value.placeholder')}
                        />
                      </ListItemText>
                    </MenuItem>
                  </MenuList>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button
            mode={buttonModes.SECONDARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.cancel')}
            icon={<CloseIcon />}
            onClick={onDialogClose}
          />
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            type={buttonTypes.BUTTON}
            text={t('common.add')}
            onClick={onConditionSave}
            icon={<Icon name={iconNames.PLUS} size={14} />}
          />
        </DialogActions>
      </Dialog>
    )
  )
}

SimulationConditionDialog.displayName = 'SimulationConditionDialog'

SimulationConditionDialog.propTypes = {
  variables: PropTypes.arrayOf(PropTypes.object),
  resources: PropTypes.arrayOf(PropTypes.object),
  parameters: PropTypes.arrayOf(PropTypes.object),
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  defaultCondition: PropTypes.object
}

SimulationConditionDialog.defaultProps = {
  variables: [],
  resources: [],
  parameters: [],
  openDialog: false,
  setOpenDialog: null,
  onSave: () => {},
  defaultCondition: null
}
